import React, { useRef, useEffect } from 'react';
import { X, Gift, Calendar, Info, AlertTriangle, Check, MapPin, Clock, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface GiveawayTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GiveawayTermsModal: React.FC<GiveawayTermsModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
            ref={modalRef}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center">
                <Gift className="h-5 w-5 text-ta-green mr-2" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Giveaway Terms & Conditions</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-4 sm:p-6 max-h-[calc(90vh-4rem)]">
              <div className="space-y-6">
                {/* Overview */}
                <div className="bg-white dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Giveaway Overview</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    TravelCord is giving away 19 free safari experiences across Uganda to lucky winners. This promotion is designed to showcase the beauty of Uganda and promote sustainable tourism in the region.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Calendar className="h-5 w-5 text-ta-green mr-2" />
                        <h4 className="font-medium text-gray-900 dark:text-white">Duration</h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Giveaway runs throughout 2025 with one winner selected each month</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="h-5 w-5 text-ta-green mr-2" />
                        <h4 className="font-medium text-gray-900 dark:text-white">Eligibility</h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Open to all adults 18+ worldwide with valid travel documents</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <MapPin className="h-5 w-5 text-ta-green mr-2" />
                        <h4 className="font-medium text-gray-900 dark:text-white">Location</h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Various destinations across Uganda including national parks</p>
                    </div>
                  </div>
                </div>
                
                {/* Prize Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Prize Details</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green/10 flex items-center justify-center mt-0.5">
                        <Check className="h-4 w-4 text-ta-green" />
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">5-Day Guided Safari</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">A comprehensive 5-day safari experience with professional guides</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green/10 flex items-center justify-center mt-0.5">
                        <Check className="h-4 w-4 text-ta-green" />
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Accommodation</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">4 nights in mid-range lodges/hotels (shared twin/double room basis)</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green/10 flex items-center justify-center mt-0.5">
                        <Check className="h-4 w-4 text-ta-green" />
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Meals</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Full board (breakfast, lunch, and dinner) throughout the safari</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green/10 flex items-center justify-center mt-0.5">
                        <Check className="h-4 w-4 text-ta-green" />
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Transportation</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">4x4 safari vehicle with experienced driver/guide</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green/10 flex items-center justify-center mt-0.5">
                        <Check className="h-4 w-4 text-ta-green" />
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Park Fees</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">All national park entrance fees and activity permits</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Info className="h-5 w-5 text-yellow-400 dark:text-yellow-500" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Not Included</h4>
                        <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                          <ul className="list-disc pl-5 space-y-1">
                            <li>International or domestic flights</li>
                            <li>Visa fees</li>
                            <li>Travel insurance</li>
                            <li>Personal expenses and gratuities</li>
                            <li>Alcoholic beverages</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* How to Enter */}
                <div className="bg-white dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">How to Enter</h3>
                  
                  <ol className="space-y-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green flex items-center justify-center text-white font-bold text-xs mt-0.5">
                        1
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Submit Your Email</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Enter your valid email address in the giveaway form on our homepage</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green flex items-center justify-center text-white font-bold text-xs mt-0.5">
                        2
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Confirm Your Entry</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Click the confirmation link sent to your email to validate your entry</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green flex items-center justify-center text-white font-bold text-xs mt-0.5">
                        3
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Share for Bonus Entries (Optional)</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Share the giveaway on social media for additional entries</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-ta-green flex items-center justify-center text-white font-bold text-xs mt-0.5">
                        4
                      </div>
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Winner Selection</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Winners will be randomly selected at the end of each month</p>
                      </div>
                    </li>
                  </ol>
                </div>
                
                {/* Terms & Conditions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Terms & Conditions</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Eligibility</h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                        <li>Open to individuals 18 years or older</li>
                        <li>Valid passport with at least 6 months validity required</li>
                        <li>Employees of TravelCord and their immediate family members are not eligible</li>
                        <li>No purchase necessary to enter</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Prize Redemption</h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                        <li>Prize must be redeemed within the 2025 calendar year</li>
                        <li>Booking subject to availability and blackout dates may apply</li>
                        <li>Prize is non-transferable and cannot be exchanged for cash</li>
                        <li>Winners must book at least 60 days in advance of desired travel date</li>
                        <li>TravelCord reserves the right to substitute any prize with another of equivalent value</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Winner Selection</h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                        <li>Winners will be selected by random drawing</li>
                        <li>Winners will be notified via email within 7 days of selection</li>
                        <li>Winners must respond within 14 days to claim their prize</li>
                        <li>If a winner fails to respond within the timeframe, an alternate winner will be selected</li>
                      </ul>
                    </div>
                    
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500" />
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-red-800 dark:text-red-200">Important Notice</h4>
                          <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                            <p>
                              TravelCord reserves the right to verify the eligibility of winners. Any attempt to manipulate the giveaway process will result in disqualification. TravelCord reserves the right to cancel or modify the giveaway if fraud or technical failures compromise the integrity of the giveaway.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* FAQ */}
                <div className="bg-white dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Can I enter multiple times?</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">No, only one entry per person is allowed. Multiple entries from the same person will be disqualified.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Can I choose my travel dates?</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Yes, winners can select their preferred travel dates in 2025, subject to availability and blackout dates.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Can I bring a guest?</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">The prize is for one person only. Guests can join but will need to pay the regular tour price.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">How will winners be announced?</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Winners will be notified via email and announced on our social media channels (with permission).</p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">What if I can't travel in 2025?</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Unfortunately, the prize must be redeemed in 2025. If you're unable to travel during this period, you may forfeit your prize.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GiveawayTermsModal;